<template>
  <div ref="targetRef" class="effect-card-cover absolute inset-0">
    <img
      v-if="displaySrc"
      ref="imgRef"
      :src="displaySrc"
      :alt="alt"
      loading="lazy"
      decoding="async"
      class="effect-card-cover__media block"
      @load="onImageLoad"
    />
    <div v-else class="w-full h-full bg-white/5" aria-hidden="true" />

    <!-- 动图加载完成前显示视频标识 -->
    <div
      v-if="showVideoBadge"
      class="absolute top-2 z-10 flex items-center justify-center size-7 rounded-full bg-black/55 backdrop-blur-sm text-white pointer-events-none"
      :class="reserveDebugSpace ? 'right-14' : 'right-2'"
      title="Video effect"
    >
      <UIcon name="material-symbols-light:slow-motion-video-rounded" class="size-6" />
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch, nextTick } from "vue";
import { useInViewport } from "@/composables/useLazyLoad";
import { resolvePublicR2Url } from "@/utils/r2Url";

const props = defineProps({
  effect: {
    type: Object,
    required: true,
  },
  effectType: {
    type: String,
    default: "video",
  },
  scrollRoot: {
    type: Object,
    default: null,
  },
  alt: {
    type: String,
    default: "",
  },
  /** 为 Debug 按钮预留右上角空间 */
  reserveDebugSpace: {
    type: Boolean,
    default: false,
  },
});

const { targetRef, isInViewport } = useInViewport({
  root: props.scrollRoot,
  rootMargin: "240px 0px",
});

const imgRef = ref(null);
const animatedLoaded = ref(false);

const displaySrc = computed(() => {
  const isVideo = props.effectType === "video";
  const staticSrc = resolvePublicR2Url(props.effect?.cover_url || "");
  if (!isInViewport.value) {
    return staticSrc;
  }
  if (isVideo && props.effect?.cover_gif) {
    return resolvePublicR2Url(props.effect.cover_gif);
  }
  return staticSrc;
});

const isAnimatedSrc = computed(
  () =>
    isInViewport.value &&
    props.effectType === "video" &&
    !!props.effect?.cover_gif &&
    displaySrc.value === resolvePublicR2Url(props.effect.cover_gif)
);

const showVideoBadge = computed(
  () => props.effectType === "video" && !!props.effect?.cover_gif && !animatedLoaded.value
);

const markAnimatedLoadedIfReady = () => {
  if (!isAnimatedSrc.value) return;
  const img = imgRef.value;
  if (img?.complete && img.naturalWidth > 0) {
    animatedLoaded.value = true;
  }
};

const onImageLoad = () => {
  if (isAnimatedSrc.value) {
    animatedLoaded.value = true;
  }
};

watch(displaySrc, () => {
  animatedLoaded.value = false;
  nextTick(markAnimatedLoadedIfReady);
});

watch(isInViewport, (visible) => {
  animatedLoaded.value = false;
  if (visible) {
    nextTick(markAnimatedLoadedIfReady);
  }
});
</script>

<style scoped>
.effect-card-cover {
  width: 100%;
  height: 100%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.05);
}

.effect-card-cover__media {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
</style>
